import { useState } from 'react'
import { Download, Pencil, Percent, Plus, Upload } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, ImportDialog, PageHeader, PermissionGate,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { usePayElements } from '@/features/payroll'
import { downloadFile, toApiProblem } from '@/lib/api/client'
import { bulkPaths } from '@/lib/bulk'
import { useLoanMutations, useLoanTypes } from '../api'
import { INTEREST_METHOD_LABELS } from '../types'
import type { InterestMethod, LoanTypeView } from '../types'
import { Select } from '@/components/ui/select'

/**
 * What may be borrowed, and on what terms.
 *
 * <p>Every limit is optional and nothing has a default. A maximum invented here is a limit
 * somebody's application is refused against for no reason anybody can explain.
 */
export function LoanTypesPage() {
  useBreadcrumb()
  const types = useLoanTypes()
  const elements = usePayElements()
  const { saveType, retireType } = useLoanMutations()

  const [draft, setDraft] = useState<{
    id?: string
    code: string; name: string; interestMethod: InterestMethod; interestRate: string
    maximumAmount: string; maximumTenorMonths: string; deductionElementId: string
  } | null>(null)
  const [retiring, setRetiring] = useState<LoanTypeView | null>(null)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Loan types"
        subtitle="What may be borrowed, over how long, and on what interest basis."
        actions={
          <PermissionGate permission="loan:configure">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void downloadFile(
                  bulkPaths('/v1/payroll/masters/loan-types').export, 'loan-types.xlsx')
              }}
            >
              <Download className="size-4" aria-hidden="true" />
              Export
            </Button>
            <Button type="button" variant="outline" onClick={() => setImporting(true)}>
              <Upload className="size-4" aria-hidden="true" />
              Import
            </Button>
            <Button
              type="button"
              onClick={() => {
                setError(null)
                setDraft({
                  code: '', name: '', interestMethod: 'NONE', interestRate: '',
                  maximumAmount: '', maximumTenorMonths: '', deductionElementId: '',
                })
              }}
            >
              <Plus className="size-4" aria-hidden="true" />
              New type
            </Button>
          </PermissionGate>
        }
      />

      <ImportDialog
        open={importing}
        onOpenChange={setImporting}
        what="loan types"
        prefix="/v1/payroll/masters/loan-types"
        invalidateKey={['loans']}
      />

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(open) => { if (!open) setRetiring(null) }}
        title={`Retire ${retiring?.name ?? 'this type'}?`}
        description="It stops being offered for new applications. Every loan already written under it keeps its schedule and its explanation — this is never a delete."
        confirmLabel="Retire it"
        loading={retireType.isPending}
        error={error}
        onConfirm={() => {
          if (!retiring) return
          setError(null)
          retireType.mutate(retiring.id, {
            onSuccess: () => setRetiring(null),
            onError: (caught) => setError(
              toApiProblem(caught)?.detail ?? 'That type could not be retired.'),
          })
        }}
      />

      <Card>
        {types.isPending && <Skeleton className="m-4 h-24" />}
        {types.isError && <ErrorState error={types.error} onRetry={() => void types.refetch()} />}
        {types.isSuccess && types.data.length === 0 && (
          <EmptyState
            title="No loan type is defined"
            description="Nothing can be applied for until one exists, and each says how interest is worked out."
          />
        )}
        {types.isSuccess && types.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Code</th>
                  <th className="p-3 text-start font-medium">Name</th>
                  <th className="p-3 text-start font-medium">Interest</th>
                  <th className="p-3 text-end font-medium">Maximum</th>
                  <th className="p-3 text-end font-medium">Longest</th>
                  <th className="p-3 text-start font-medium">Deducted on</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {types.data.map((type) => (
                  <tr key={type.id} className={`border-b border-slate-100 ${type.active ? '' : 'text-slate-400'}`}>
                    <td className="p-3 font-mono">{type.code}</td>
                    <td className="p-3">{type.name}</td>
                    <td className="p-3 text-slate-600">
                      {INTEREST_METHOD_LABELS[type.interestMethod as InterestMethod]
                        ?? type.interestMethod}
                      {type.interestRate != null && ` — ${type.interestRate}%`}
                    </td>
                    <td className="p-3 text-end">
                      {type.maximumAmount ?? <span className="text-slate-500">no cap</span>}
                    </td>
                    <td className="p-3 text-end">
                      {type.maximumTenorMonths
                        ? `${type.maximumTenorMonths} months`
                        : <span className="text-slate-500">no limit</span>}
                    </td>
                    <td className="p-3">
                      {type.deductionElementId
                        ? elements.data?.find((element) => element.id === type.deductionElementId)?.name
                          ?? 'an element'
                        : <span className="text-amber-700">nothing — cannot be deducted</span>}
                    </td>
                    <td className="p-3 text-end">
                      <PermissionGate permission="loan:configure">
                        <Button
                          type="button" variant="ghost" size="sm"
                          aria-label={`Edit ${type.name}`}
                          onClick={() => {
                            setError(null)
                            setDraft({
                              id: type.id,
                              code: type.code,
                              name: type.name,
                              interestMethod: type.interestMethod as InterestMethod,
                              interestRate: type.interestRate == null
                                ? '' : String(type.interestRate),
                              maximumAmount: type.maximumAmount == null
                                ? '' : String(type.maximumAmount),
                              maximumTenorMonths: type.maximumTenorMonths == null
                                ? '' : String(type.maximumTenorMonths),
                              deductionElementId: type.deductionElementId ?? '',
                            })
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {type.active && (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => { setError(null); setRetiring(type) }}
                          >
                            Retire
                          </Button>
                        )}
                      </PermissionGate>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <Percent className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Flat and reducing balance at the same rate are not the same price: flat charges the
        whole rate on the original amount for the whole term. The method is named everywhere
        rather than only the rate, so the two are not compared as if they were equivalent.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title={draft?.id ? 'Edit loan type' : 'New loan type'}
        description="A type charging interest needs a rate — without one every schedule would compute none."
        submitLabel={draft?.id ? 'Save changes' : 'Create it'}
        loading={saveType.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await saveType.mutateAsync({
                id: draft.id,
                code: draft.code,
                name: draft.name,
                interestMethod: draft.interestMethod,
                interestRate: draft.interestRate ? Number(draft.interestRate) : null,
                maximumAmount: draft.maximumAmount ? Number(draft.maximumAmount) : null,
                maximumTenorMonths: draft.maximumTenorMonths
                  ? Number(draft.maximumTenorMonths) : null,
                deductionElementId: draft.deductionElementId || null,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That type could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="type-code">Code</Label>
                <Input
                  id="type-code" className="font-mono" value={draft.code}
                  onChange={(event) =>
                    setDraft({ ...draft, code: event.target.value.toUpperCase() })}
                />
              </div>
              <div>
                <Label htmlFor="type-name">Name</Label>
                <Input
                  id="type-name" value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="type-interest">How interest works</Label>
                <Select
                  id="type-interest"
                  className="w-full"
                  value={draft.interestMethod}
                  onChange={(event) =>
                    setDraft({ ...draft, interestMethod: event.target.value as InterestMethod })}
                >
                  {Object.entries(INTEREST_METHOD_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </div>
              {draft.interestMethod !== 'NONE' && (
                <div>
                  <Label htmlFor="type-rate">Annual rate %</Label>
                  <Input
                    id="type-rate" type="number" value={draft.interestRate}
                    onChange={(event) => setDraft({ ...draft, interestRate: event.target.value })}
                  />
                </div>
              )}
              <div>
                <Label htmlFor="type-max">Most that may be borrowed</Label>
                <Input
                  id="type-max" type="number" value={draft.maximumAmount}
                  placeholder="Leave blank for no cap"
                  onChange={(event) => setDraft({ ...draft, maximumAmount: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="type-tenor">Longest term, in months</Label>
                <Input
                  id="type-tenor" type="number" value={draft.maximumTenorMonths}
                  placeholder="Leave blank for no limit"
                  onChange={(event) =>
                    setDraft({ ...draft, maximumTenorMonths: event.target.value })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="type-element">Deducted on which pay element</Label>
              <Select
                id="type-element"
                className="w-full"
                value={draft.deductionElementId}
                onChange={(event) =>
                  setDraft({ ...draft, deductionElementId: event.target.value })}
              >
                <option value="">Choose an element…</option>
                {elements.data?.filter((element) => element.elementType === 'DEDUCTION')
                  .map((element) => (
                    <option key={element.id} value={element.id}>
                      {element.name} ({element.code})
                    </option>
                  ))}
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                Without one, nothing is deducted through payroll and the loan has to be repaid
                by hand. Only deduction elements are offered.
              </p>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
